import dayjs from "dayjs";
import * as Haptics from "expo-haptics";
import { Platform, Vibration } from "react-native";
import Toast, { ToastType } from "react-native-toast-message";
import { INVESTMENT_TYPES, PAYMENT_MODE, TOAST_DURATION } from "../constants";
import { ROUTES } from "../constants/routes";
import { PlanDescOption, RawOption, RawPlan, SchemeResponse } from "../types";
import { FundDetails, MODES_OF_INVESTMENT } from "../types/invest";
import { PortfolioItem } from "../types/portfolio";
import { ProductDetail } from "../types/systematicPlans";
import { SipDetails } from "../types/transaction";

export type TransactionAmountInput = {
  td_amt?: string | number | null;
  td_units?: string | number | null;
  sipamount?: string | number | null;
};

const parseTransactionNumeric = (value?: string | number | null): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const trimmed = value.trim();
  if (!trimmed || trimmed === "-") return 0;

  const parsed = Number(trimmed.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const isBlankTransactionAmount = (value?: string | number | null): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "number") return value === 0;
  const trimmed = value.trim();
  return trimmed === "" || trimmed === "-";
};

const formatTransactionUnits = (value?: string | number | null): string | null => {
  if (value === null || value === undefined) return null;
  const units = typeof value === "number" ? String(value) : value.trim();
  if (!units || units === "-") return null;
  return units;
};

/** Display amount for transaction list/details — matches mobile TransactionHistory logic. */
export const formatTransactionDisplayAmount = (item: TransactionAmountInput): string => {
  const tdAmount = parseTransactionNumeric(item.td_amt);
  const sipAmount = parseTransactionNumeric(item.sipamount);
  const units = formatTransactionUnits(item.td_units);

  if ((isBlankTransactionAmount(item.td_amt) || tdAmount === 0) && units) {
    return `${units} units`;
  }

  if (tdAmount === 0 && sipAmount > 0) {
    return `₹ ${sipAmount.toLocaleString("en-IN")}`;
  }

  if (isBlankTransactionAmount(item.td_amt)) {
    return "₹ 0";
  }

  return `₹ ${tdAmount.toLocaleString("en-IN")}`;
};

export const numberToIndianCurrency = (
  input: number | string,
  maximumFractionDigits = 2,
) => {
  if (isNaN(Number(input))) {
    return "-";
  }

  return Number(input)
    .toLocaleString("en-IN", {
      style: "currency",
      currency: "inr",
      maximumFractionDigits,
    })
    .replace("₹", "₹ ");
};

export const formatPlanText = (text: string | undefined) => {
  if (!text) return "";
  const cleaned = text.replace(/\bplan\b/gi, "").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
};

/** Trim and collapse consecutive spaces — API/KYC names often include extra whitespace. */
export const normalizeName = (name?: string | null): string =>
  (name ?? "").trim().replace(/\s+/g, " ");

const formatPlanLabel = (plandesc: string, optiondesc: string): string => {
  const planType = plandesc?.split(" ")[2];
  return planType ? `${planType} - ${optiondesc}` : optiondesc;
};

export const parsePlanValue = (value: string): [plan: string, option: string] => {
  const [plan = "", option = ""] = value?.split("-");
  return [plan, option];
};

export const getGrowthOption = (options: PlanDescOption[]): PlanDescOption =>
  options?.find((option) => option?.label?.includes("Growth")) ??
  options?.[0] ?? {
    planDesc: "",
    label: "",
    value: "",
  };

export const processOptionsFromResponse = (
  response: SchemeResponse,
  mode: MODES_OF_INVESTMENT | string,
): PlanDescOption[] => {
  let plans: RawPlan[] =
    mode === MODES_OF_INVESTMENT.DIRECT
      ? response?.DIRECT
      : mode === MODES_OF_INVESTMENT.REGULAR
        ? response?.REGULAR
        : (response as any)?.[mode];

  if (!plans && response) {
    const keys = Object.keys(response);
    if (keys.length > 0) {
      plans = (response as any)?.[keys[0]];
    }
  }

  return (
    plans?.flatMap((plan: RawPlan) =>
      plan?.options?.map((option: RawOption) => ({
        label: formatPlanLabel(plan?.plandesc, option?.optiondesc),
        value: `${plan?.plancode}-${option?.optioncode}`,
        planDesc: plan?.plandesc,
      })),
    ) ?? []
  );
};

export const formatNumberWithCommas = (num: number, decimals: number = 2): string => {
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export function maskEmail(email: string) {
  if (!email || typeof email !== "string") {
    return " - ";
  }
  const [localPart, domain] = email.split("@");
  if (!domain) return email;

  let masked: string;

  if (localPart.length <= 2) {
    masked = "*".repeat(localPart.length);
  } else if (localPart.length <= 4) {
    masked = `${localPart[0]}${"*".repeat(localPart.length - 2)}${localPart[localPart.length - 1]}`;
  } else {
    const start = localPart.slice(0, 2);
    const end = localPart.slice(-2);
    masked = `${start}${"*".repeat(localPart.length - 4)}${end}`;
  }

  return `${masked}@${domain}`;
}

export function maskEmailWithDomain(email: string): string {
  if (!email || typeof email !== "string") {
    return email;
  }
  const atIndex = email.lastIndexOf("@");
  const dotIndex = email.lastIndexOf(".");

  if (atIndex === -1 || dotIndex === -1 || dotIndex < atIndex) {
    return email;
  }
  const localPart = email.slice(0, atIndex);
  const domainName = email.slice(atIndex + 1, dotIndex);
  const domainExt = email.slice(dotIndex); // includes the dot

  // Mask local part: show last 3 characters, mask the rest with 'X'
  const visibleLocal = localPart.slice(-3);
  const maskedLocal =
    "*".repeat(Math.max(localPart.length - 3, 0)).substring(0, 6) + visibleLocal;

  // Mask domain name: show last 3 characters, mask the rest with 'X'
  const visibleDomain = domainName.slice(-3);
  const maskedDomain =
    "*".repeat(Math.max(domainName.length - 3, 0)).substring(0, 4) + visibleDomain;

  return `${maskedLocal}@${maskedDomain}${domainExt}`;
}

export function maskMobile(mobile?: string) {
  if (!mobile || typeof mobile !== "string") {
    return " - ";
  }

  // If mobile is too short to mask meaningfully, return as is
  if (mobile.length < 5) {
    return mobile;
  }

  const start = mobile.slice(0, 2);
  const end = mobile.slice(-3);
  const masked = "*".repeat(Math.max(0, mobile.length - (start.length + end.length)));

  return `${start}${masked}${end}`;
}

export const maskBankAccountNumber = (
  accountNumber: string,
  visibleDigits = 4,
): string => {
  const str = String(accountNumber).trim();

  if (!str) return "";

  const length = str.length;
  if (length <= visibleDigits) return str;

  const maskedPart = "*".repeat(length - visibleDigits);
  const visiblePart = str.slice(-visibleDigits);

  return `${maskedPart}${visiblePart}`;
};

type ShowToastOptions = {
  enableHaptics?: boolean;
  impactStyle?: Haptics.ImpactFeedbackStyle;
  vibrationDurationMs?: number;
  visibilityTime?: number;
};

export const showToast = (
  type: ToastType,
  title: string,
  options: ShowToastOptions = {},
) => {
  const {
    enableHaptics = true,
    impactStyle = Haptics.ImpactFeedbackStyle.Heavy,
    vibrationDurationMs = 50,
    visibilityTime,
  } = options;

  if (enableHaptics && Platform.OS !== "web") {
    Haptics.impactAsync(impactStyle).catch(() => undefined);
    Vibration.vibrate(vibrationDurationMs);
  }

  const isWeb = Platform.OS === "web";
  const isInvestorServicesWebPage =
    isWeb &&
    typeof window !== "undefined" &&
    (window.location?.href?.includes("investor-services") ||
      window.location?.pathname?.includes("investor-services"));

  const displayDuration = isWeb && isInvestorServicesWebPage ? 10000 : TOAST_DURATION;
  const toastVisibilityTime = visibilityTime ?? displayDuration;

  Toast.show({
    type: "invescoToast",
    text1: title,
    position: "top",
    visibilityTime: toastVisibilityTime,
    autoHide: true,
    swipeable: false,
    topOffset: Platform.OS === "web" ? 30 : 60,
    props: {
      toastType: type,
    },
  });
};
export function formatNumberWithZero(number: any) {
  if (isNaN(number)) return "00:00";
  return (
    `${Math.floor(Number(number) / 60)}`.padStart(2, "0") +
    ":" +
    `${Number(number) % 60}`.padStart(2, "0")
  );
}

export function formatIsoDate(isoDate: string, format: string = "DD/MM/YYYY"): string {
  if (!isoDate) return "";
  const date = dayjs(isoDate);
  return date.isValid() ? date.format(format) : "";
}

export const checkIfMinor = (
  dob: Date | number | string | dayjs.Dayjs | null | undefined,
): boolean => {
  if (!dob) {
    return false;
  }
  const birthDate = dayjs(dob);
  if (!birthDate.isValid()) {
    return false;
  }
  const today = dayjs();
  const age = today.diff(birthDate, "year");
  return age < 18;
};

export const getEligiblePaymentModes = ({
  isLumpsum,
  isSip,
}: {
  isLumpsum: boolean;
  isSip: boolean;
}) => {
  const eligiblePaymentModes = [];

  if (isLumpsum) {
    eligiblePaymentModes.push(PAYMENT_MODE.UPI);
  }

  if (isSip) {
    eligiblePaymentModes.push(PAYMENT_MODE.UPI_MANDATES);
  }

  eligiblePaymentModes.push(PAYMENT_MODE.ENACH);
  eligiblePaymentModes.push(PAYMENT_MODE.NET_BANKING);
  if (isLumpsum) {
    eligiblePaymentModes.push(PAYMENT_MODE.NEFT_RTGS);
  }

  return eligiblePaymentModes;
};

export const toTitleCase = (str: string | null | undefined) => {
  if (!str) return "";
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

type CycleConfigLike = {
  minimumAmount?: number | null;
  cycleIds?: string[] | null;
  minimumInstallments?: number | null;
  minimumGapDays?: number | null;
  active?: string | null;
  maxGap?: number | null;
  multiples?: number | null;
};

const toSafeNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const selectCycleConfigByAmount = <T extends CycleConfigLike>(
  configs?: T[] | null,
  amount?: number | null,
): T | undefined => {
  if (!configs?.length) return undefined;

  const activeConfigs = configs.filter(
    (config) => !config?.active || String(config.active).toUpperCase() === "Y",
  );
  const source = activeConfigs.length > 0 ? activeConfigs : configs;

  const sorted = [...source].sort(
    (a, b) => toSafeNumber(a?.minimumAmount) - toSafeNumber(b?.minimumAmount),
  );

  if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
    return sorted[0];
  }

  const numericAmount = Number(amount);
  const eligible = sorted.filter(
    (config) => numericAmount >= toSafeNumber(config?.minimumAmount),
  );

  return eligible.length > 0 ? eligible[eligible.length - 1] : sorted[0];
};

export const formatAssetClass = (category: string | null | undefined) => {
  if (!category) return "";
  const cat = category.toUpperCase();
  if (cat === "DEBT" || cat === "LIQUID") return "Fixed income";
  return toTitleCase(cat);
};

export type FrequencyType =
  | "Daily"
  | "Weekly"
  | "Monthly"
  | "Quarterly"
  | "Half-Yearly"
  | "Yearly";

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Maps a frequency string (e.g., "MONTHLY", "Daily", "Q") to its corresponding API code.
 * @param frequency The frequency string to map
 * @returns The API code (M, D, Q, W, H, Y)
 */
export const getFrequencyCode = (frequency: string | undefined | null): string => {
  if (!frequency) return "M"; // Default to Monthly

  const freq = frequency.toUpperCase();

  if (freq.startsWith("D")) return "D"; // Daily
  if (freq.startsWith("W")) return "W"; // Weekly
  if (freq.startsWith("Q")) return "Q"; // Quarterly
  if (freq.startsWith("H")) return "H"; // Half-Yearly
  if (freq.startsWith("Y")) return "Y"; // Yearly
  if (freq.startsWith("M")) return "M"; // Monthly

  return "M"; // Fallback to Monthly
};

export function getInstallmentsPerYear(frequency: FrequencyType) {
  if (frequency === "Daily") {
    const currentYear = new Date().getFullYear();
    return isLeapYear(currentYear) ? 366 : 365;
  } else if (frequency === "Weekly") {
    return 52;
  } else if (frequency === "Monthly") {
    return 12;
  } else if (frequency === "Quarterly") {
    return 4;
  }
  return 0;
}

export const formatNameString = (name: string) => {
  return name
    ?.toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getRouteAndParam = (data: string): { route: ROUTES; param: string } => {
  const toRoute = data?.toLowerCase().trim();
  const rotueMap: Record<string, { route: ROUTES; param: string }> = {
    switch: { route: ROUTES.SWITCH, param: "" },
    "switch fund": { route: ROUTES.SWITCH, param: "" },
    sip: { route: ROUTES.INVEST, param: INVESTMENT_TYPES.SIP },
    "start sip": { route: ROUTES.INVEST, param: INVESTMENT_TYPES.SIP },
    portfolio: { route: ROUTES.PORTFOLIO, param: "" },
    lumpsum: { route: ROUTES.INVEST, param: INVESTMENT_TYPES.LUMPSUM },
    "start swp": { route: ROUTES.SWP, param: "" },
    redeem: { route: ROUTES.REDEEM, param: "" },
    "start stp": { route: ROUTES.STP, param: "" },
  };
  if (rotueMap[toRoute]) {
    return rotueMap[toRoute];
  } else {
    return { route: ROUTES.DASHBOARD, param: "" };
  }
};

export const getSchemeDetails = (
  {
    scheme,
    planCode,
    optionCode,
    folio,
  }: {
    scheme: string;
    planCode: string;
    optionCode: string;
    folio?: string;
  },
  list: PortfolioItem[] | FundDetails[],
) => {
  if (!list) return undefined;

  const isAllowed = (item: any) =>
    item?.purallow === "Y" ||
    item?.fm_purallow === "Y" ||
    item?.sipallow === "Y" ||
    item?.fm_sipallow === "Y"
      ? 1
      : 0;

  const exactMatches = list.filter(
    (schemeDetails) =>
      ("scheme" in schemeDetails
        ? (schemeDetails as FundDetails).scheme === scheme ||
          (schemeDetails as any).fm_scheme === scheme
        : (schemeDetails as PortfolioItem).SchemeCode === scheme) &&
      ("plan" in schemeDetails
        ? (schemeDetails as FundDetails).plan === planCode ||
          (schemeDetails as any).fm_plan === planCode
        : (schemeDetails as PortfolioItem).PlanCode === planCode) &&
      ("option" in schemeDetails
        ? (schemeDetails as FundDetails).option === optionCode ||
          (schemeDetails as any).fm_option === optionCode
        : (schemeDetails as PortfolioItem).OptionCode === optionCode) &&
      (folio ? (schemeDetails as PortfolioItem).Folio === folio : true),
  );

  if (exactMatches.length > 0) {
    const allowedExact = exactMatches.find((item) => isAllowed(item) === 1);
    if (allowedExact) return allowedExact;
    // Exact match found (folio + scheme + plan + option) but none is "allowed"
    // (portfolio items don't have purallow/sipallow). Still return the exact match
    // to avoid folio bleeding through the scheme-only fallback below.
    return exactMatches[0];
  }

  const schemeMatches = list.filter((schemeDetails) =>
    "scheme" in schemeDetails
      ? (schemeDetails as FundDetails).scheme === scheme ||
        (schemeDetails as any).fm_scheme === scheme
      : (schemeDetails as PortfolioItem).SchemeCode === scheme,
  );

  if (schemeMatches.length > 0) {
    return [...schemeMatches].sort((a: any, b: any) => isAllowed(b) - isAllowed(a))[0];
  }

  return exactMatches[0];
};
export const formatAmount = (amount: number): string => {
  if (!amount && amount !== 0) return "₹0.00";

  const num = parseFloat(amount.toString());
  const truncate = (n: number) => (Math.floor(n * 100) / 100).toFixed(2);

  if (num >= 10000000) {
    return `₹${truncate(num / 10000000)}Cr`;
  } else if (num >= 100000) {
    return `₹${truncate(num / 100000)}L`;
  } else if (num >= 1000) {
    return `₹${truncate(num / 1000)}K`;
  } else {
    return `₹${truncate(num)}`;
  }
};

export const formatCompactAmountForAnimationTruncated = (
  amount: number,
): { value: number; suffix: string } => {
  const num = Number(amount || 0);
  const truncateToTwoDecimals = (n: number) => Math.floor(n * 100) / 100;

  if (num >= 10000000) {
    return {
      value: truncateToTwoDecimals(num / 10000000),
      suffix: "Cr",
    };
  }
  if (num >= 100000) {
    return {
      value: truncateToTwoDecimals(num / 100000),
      suffix: "L",
    };
  }
  if (num >= 1000) {
    return {
      value: truncateToTwoDecimals(num / 1000),
      suffix: "K",
    };
  }

  return {
    value: truncateToTwoDecimals(num),
    suffix: "",
  };
};

export const matchExistingToNewSchemeType = (portfolio: PortfolioItem): FundDetails => {
  return {
    id:
      portfolio.PlanCode + " " + portfolio.OptionCode + " " + portfolio.SchemeCode || "",
    fund: portfolio.Fund || "",
    scheme: portfolio.SchemeCode || "",
    schdesc: portfolio.SchemeName || "",
    plandesc: portfolio.PlanName || "",
    optiondesc: portfolio.OptionName || "",
    plan: portfolio.PlanCode || "",
    plnmode: portfolio.PlanMode || "",
    option: portfolio.OptionCode || "",
    mcrid: "",
    category: portfolio.SchemeCategory || "",
    purallow: "Y",
    redallow: "Y",
    sipallow: "Y",
    stpiallow: "N",
    swiallow: "N",
    swoallow: "N",
    latiallow: "N",
    latoallow: "N",
    minAmt: 0,
    multiples: 0,
    nav: portfolio?.Nav,
    navDate: portfolio?.NavDate,
    navPercentage: portfolio?.DailyGainLoss?.toString(),
    inceptionDate: "", // Added to fix type
  };
};

/**
 * Adds working days (skipping Saturday and Sunday) to a given start date
 * @param startDate The starting date (Dayjs object or string)
 * @param daysToAdd Number of working days to add
 * @returns Dayjs object representing the resulting date
 */
export const addWorkingDays = (startDate: any, daysToAdd: number) => {
  let currentDate = dayjs(startDate);
  let daysAdded = 0;

  while (daysAdded < daysToAdd) {
    currentDate = currentDate.add(1, "day");

    // 0 is Sunday, 6 is Saturday in Dayjs
    const dayOfWeek = currentDate.day();

    // Skip Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++;
    }
  }

  return currentDate;
};

export const getYearsAndMonths = (
  dateString: string,
): { years: number; months: number } => {
  if (!dateString) return { years: 0, months: 0 };

  const today = dayjs();
  const target = dayjs(dateString);

  const from = today.isBefore(target) ? today : target;
  const to = today.isBefore(target) ? target : today;

  const years = to.diff(from, "year");
  const months = to.diff(from.add(years, "year"), "month");

  return { years, months };
};

export const OptionFormate = (option: string) => {
  return option?.slice(0, 1).toUpperCase();
};

export const PlanFormate = (plan: string) => {
  return plan?.slice(0, 3).toUpperCase();
};

/**
 * Strips plan-type keywords ("Regular", "Direct", "Plan") from a plan description
 * when the plan type is already conveyed by a separate REG/DIR indicator (i.e. when
 * a valid distributor ARN is present). Both "regular" and "direct" are removed because
 * the dedicated REG/DIR pill already communicates the plan type — keeping either word
 * in the description would be redundant.
 *
 * Uses word-boundary matching so fund names that happen to contain these words in a
 * non-plan-type context (e.g. "Regular Income") are only affected when the word stands
 * alone or is followed by "plan".
 *
 * After keyword removal, strips orphaned separators (dashes, pipes, commas) and
 * collapses extra whitespace so no residual punctuation leaks into the UI.
 *
 * @param planDesc The raw plan description
 * @param hasArn  Whether a valid distributor ARN is present (plan type shown separately)
 * @returns The cleaned description, or empty string if nothing meaningful remains
 */
export const cleanPlanDescription = (
  planDesc: string | undefined | null,
  hasArn: boolean,
): string => {
  if (!planDesc) return "";
  if (!hasArn) return planDesc;

  let cleaned = planDesc
    // Remove "Regular Plan" / "Direct Plan" as full phrases first (greedy)
    .replace(/\b(regular|direct)\s+plan\b/gi, "")
    // Remove standalone "Regular" or "Direct" only when bounded by word boundaries
    .replace(/\b(regular|direct)\b/gi, "")
    // Remove standalone "Plan" that may remain
    .replace(/\bplan\b/gi, "")
    // Strip orphaned separators: leading/trailing dashes, pipes, commas, dots
    .replace(/^[\s\-–—|,.]+|[\s\-–—|,.]+$/g, "")
    // Collapse internal orphaned separators surrounded by whitespace (e.g. " - ")
    .replace(/\s+[\-–—|,.]+\s+/g, " ")
    // Collapse multiple spaces
    .replace(/\s{2,}/g, " ")
    .trim();

  // If nothing meaningful remains, return empty
  if (!cleaned) return "";

  return cleaned;
};

/**
 * Formats a cleaned plan description into a short pill label for mobile display.
 * Takes the first 3 characters uppercase and appends "IDCW" if the original
 * description contains that term.
 *
 * @param planDesc The raw plan description
 * @param hasArn  Whether a valid distributor ARN is present (plan type shown separately)
 * @returns Abbreviated pill string (e.g. "GRO", "GRO IDCW") or empty string
 */
export const getFormattedPlanPill = (
  planDesc: string | undefined | null,
  hasArn: boolean,
): string => {
  const cleaned = cleanPlanDescription(planDesc, hasArn);
  if (!cleaned) return "";

  const formatted = cleaned.slice(0, 3).toUpperCase();
  return (planDesc ?? "").toLowerCase().includes("idcw")
    ? `${formatted} IDCW`
    : formatted;
};

/**
 * Capitalize text by making the first letter of each word uppercase
 * and the rest lowercase. Handles multi-word strings.
 * @param text - The text to capitalize
 * @returns Capitalized text (e.g., "hello world" -> "Hello World")
 */
export const capitalizeText = (text: string | undefined | null): string => {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Robustly extracts and parses error messages from various error object formats,
 * including nested JSON strings commonly returned by the API.
 * @param error The error object or string
 * @returns A user-friendly error message string
 */
export const getErrorMessage = (error: any): string => {
  if (!error) return "";

  if (typeof error === "string") {
    try {
      // Handle the escaped JSON string case
      const parsed = JSON.parse(error);
      if (typeof parsed === "object" && parsed !== null) {
        return getErrorMessage(parsed);
      }
    } catch (e) {
      console.error("Error parsing error string:", e);
      return error;
    }
  }

  if (typeof error === "object" && error !== null) {
    // Check for common RTK Query and API error structures
    const errorData = error.data || error;

    // Direct message in error object
    if (typeof errorData.message === "string" && errorData.message) {
      return getErrorMessage(errorData.message);
    }

    // Direct error in error object
    if (typeof errorData.error === "string" && errorData.error) {
      return errorData.error;
    }

    if (errorData.error && typeof errorData.error === "object") {
      return getErrorMessage(errorData.error);
    }

    // Fallback for serialized objects
    if (Object.keys(errorData).length > 0) {
      try {
        return JSON.stringify(errorData);
      } catch (e) {
        console.error("Error parsing error data:", e);
        return "An unexpected error occurred";
      }
    }
  }

  return String(error || "An unexpected error occurred");
};

/**
 * Calculate SIP returns
 * @param monthlyInvestment SIP amount per month (₹)
 * @param annualReturnRate Expected annual return (%) e.g. 12
 * @param years Investment duration in years
 */
export function calculateSIP(
  monthlyInvestment: number,
  annualReturnRate: number,
  years: number,
): any {
  const months: number = years * 12;
  const monthlyRate: number = annualReturnRate / 12 / 100;

  const maturityValue: number =
    monthlyInvestment *
    ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
    (1 + monthlyRate);

  const totalInvestment: number = monthlyInvestment * months;
  const totalReturns: number = maturityValue - totalInvestment;

  return {
    monthlyInvestment,
    years,
    annualReturnRate,
    totalInvestment: Math.round(totalInvestment),
    maturityValue: Math.round(maturityValue),
    totalReturns: Math.round(totalReturns),
  };
}

export const matchSystematicPlansAndInvestType = (
  productDetail: ProductDetail,
): FundDetails & SipDetails & { folioNumber: string } => {
  return {
    id: productDetail?.Ihno,
    fund: productDetail?.FundName,
    scheme: productDetail?.SchemeCode,
    schdesc: productDetail?.FromSchemeDesc || productDetail?.SchemeDesc || "",
    plandesc: productDetail?.FromPlanDesc || productDetail?.PlanDesc || "",
    optiondesc: productDetail?.OptionName,
    plan: productDetail?.FromPlanCode || productDetail?.PlanCode || "",
    plnmode: productDetail?.PlanMode || "",
    option: productDetail?.OptionName || "",
    mcrid: "",
    category: "",
    purallow: "Y",
    redallow: "Y",
    sipallow: "Y",
    stpiallow: "Y",
    swiallow: "Y",
    swoallow: "Y",
    latiallow: "Y",
    latoallow: "Y",
    minAmt: Number(productDetail?.amount || 0),
    multiples: 0,
    nav: "",
    navDate: "",
    navPercentage: "",
    inceptionDate: "",
    installmentAmount: productDetail?.InstallmentAmount || 0,
    noOfInstallments:
      Number(productDetail?.pendingInst) + Number(productDetail?.completedInst),
    isTopUp: false,
    topUpDetails: {
      frequency: "",
      amount: 0,
      maxLimit: 0,
    },
    day: "",
    frequency: productDetail?.Frequency || productDetail?.sanitizedFrequency || "",
    startDate: productDetail?.StartDate,
    endDate: productDetail?.EndDate,
    folioNumber: productDetail?.Folio || "",
  };
};

export const getCookie = (name: string): string | null => {
  const match = document.cookie?.split("; ")?.find((row) => row?.startsWith(`${name}=`));
  return match ? match.split("=")[1] : null;
};

export const clearCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const clearExploreFundCookies = () => {
  clearCookie("exploreFundData");
  clearCookie("navigatedFrom");
  clearCookie("transactionType");
};

export const matchSchemeFromCookieData = (
  decoded: string,
  schemeList: FundDetails[],
): FundDetails | null => {
  const [scheme, plan, option] = decoded?.split("-") ?? [];

  if (scheme && plan && option) {
    return (
      schemeList?.find(
        (s) => s?.scheme === scheme && s?.plan === plan && s?.option === option,
      ) ?? null
    );
  }

  const fs = schemeList?.filter((s) => s?.SchemeCode === decoded) ?? [];
  const idcwSchemes = fs?.filter((sch) => sch?.plandesc?.toLowerCase().includes("idcw"));

  if (idcwSchemes?.length) {
    return (
      idcwSchemes?.find((sch) => sch?.optiondesc?.toLowerCase() === "reinvestment") ??
      null
    );
  }

  return fs?.[0] ?? null;
};

export { isWeekdayAllowed, parseWeekdayCycleIds } from "./stpWeeklyCycle";

export const getNearestValidStpDate = (
  dateToValidate: Date,
  {
    minDate,
    maxDate,
    isStp,
    cycleIdsHasAll,
    normalizedCycleDays,
    normalizedCycleWeekdays = [],
  }: {
    minDate: dayjs.Dayjs;
    maxDate: dayjs.Dayjs;
    isStp: boolean;
    cycleIdsHasAll: boolean;
    normalizedCycleDays: number[];
    // WEEKLY cycles pass weekday indices (0=Sun..6=Sat) here instead of day-of-month.
    normalizedCycleWeekdays?: number[];
  },
): Date => {
  let candidateDate = dayjs(dateToValidate);

  if (!candidateDate.isValid() || candidateDate.isBefore(minDate, "day")) {
    candidateDate = minDate;
  }

  if (!isStp) {
    return candidateDate.toDate();
  }

  // WEEKLY: snap forward to the nearest date whose weekday is allowed.
  if (normalizedCycleWeekdays.length > 0) {
    let currentDate = candidateDate.startOf("day");
    while (currentDate.isBefore(maxDate, "day") || currentDate.isSame(maxDate, "day")) {
      if (normalizedCycleWeekdays.includes(currentDate.day())) {
        return currentDate.toDate();
      }
      currentDate = currentDate.add(1, "day");
    }
    return minDate.toDate();
  }

  if (cycleIdsHasAll || normalizedCycleDays.length === 0) {
    return candidateDate.toDate();
  }

  let currentDate = candidateDate.startOf("day");
  while (currentDate.isBefore(maxDate, "day") || currentDate.isSame(maxDate, "day")) {
    if (normalizedCycleDays.includes(currentDate.date())) {
      return currentDate.toDate();
    }
    currentDate = currentDate.add(1, "day");
  }

  return minDate.toDate();
};

export interface RetryOptions<T> {
  retries?: number;
  delay?: number;
  shouldRetry?: (error: any) => boolean;
  validate?: (result: T) => boolean;
  signal?: AbortSignal;
}

/**
 * Executes an asynchronous function with a retry mechanism.
 * @param fn The asynchronous function to execute
 * @param options Retry configuration options
 */
export async function callWithRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions<T> = {},
): Promise<T> {
  const { retries = 3, delay = 2000, shouldRetry, validate, signal } = options;

  try {
    const result = await fn();

    // Check if the result is valid (e.g., data is ready)
    if (validate && !validate(result)) {
      throw new Error("Validation failed");
    }

    return result;
  } catch (err) {
    // If the signal is aborted, don't retry and throw an error
    if (signal?.aborted) {
      throw new Error("Aborted");
    }

    // If we've run out of retries or shouldn't retry this specific error
    if (retries === 0 || (shouldRetry && !shouldRetry(err))) {
      throw err;
    }

    // Wait for the specified delay before retrying
    await new Promise((resolve) => {
      const timer = setTimeout(resolve, delay);

      // Handle signal cancellation during the delay
      const onAbort = () => {
        clearTimeout(timer);
        resolve(null);
      };

      signal?.addEventListener("abort", onAbort, { once: true });
    });

    // Check again after delay if signal was aborted
    if (signal?.aborted) {
      throw new Error("Aborted");
    }

    // Recursive call with decremented retry count
    return callWithRetry(fn, { ...options, retries: retries - 1 });
  }
}

export const getPaymentModeLabel = (mode: string | undefined): string => {
  if (!mode) return "N/A";
  const upper = mode.trim().toUpperCase();

  const modeMap: Record<string, string> = {
    // Net Banking
    DCB: "Net Banking",
    NB: "Net Banking",
    NETBANKING: "Net Banking",

    // UPI
    UPI: "UPI",
    UA: "UPI Mandate",
    UP: "UPI Mandate",
    UAUTO: "UPI Mandate",
    UATM: "UPI Mandate",

    // eNACH / OTM
    AO: "eNACH",
    KO: "eNACH",
    AOTM: "eNACH",
    KOTM: "eNACH (KOTM)",
    ENACH: "eNACH",
    NACH: "NACH",
    OTM: "One Time Mandate",
    OT: "One Time Mandate",

    // SIP modes
    // ISIP: "SIP (Net Banking)",
    // XSIP: "SIP (NACH)",
    // SIN: "SIP (First Installment)",
    // SINI: "SIP (New Folio)",
    ISIP: "Net Banking",
    XSIP: "NACH",
    SIN: "First Installment",
    SINI: "New Folio",

    // Lumpsum / Purchase
    APL: "Additional Purchase",
    NPL: "New Purchase",
    BSE: "BSE",
    NSE: "NSE",

    // Cheque / DD
    CHQ: "Cheque",
    DD: "Demand Draft",
    DC: "Debit Card",
    "DEBT CARD": "Debit Card",

    // NEFT / RTGS
    NEFT: "NEFT",
    RTGS: "RTGS",
    IMPS: "IMPS",
  };

  return modeMap[upper] ?? capitalizeText(mode);
};

const NRI_APPLICANT_CATEGORIES = [
  "21",
  "22",
  "NRI-REPATRIABLE",
  "NRI-NON-REPATRIABLE",
  "NRI-Repatriable (NRE)",
  "NRI-Non-Repatriable (NRO)",
];

export const isNRICategory = (category?: string | null): boolean => {
  if (!category) return false;
  const normalized = category.trim();
  return (
    NRI_APPLICANT_CATEGORIES.includes(normalized) ||
    normalized.toUpperCase().includes("NRI")
  );
};

// invcat is the authoritative folio category code from the backend:
//   "11" = Resident Indian (RI), "21" = NRI Repatriable (NRE), "22" = NRI Non-Repatriable (NRO).
// When present it takes priority over the older ResidentType/Category/TaxStatus
// heuristics; when absent, callers fall back to those.
export const isRIInvCat = (invcat?: string | null): boolean =>
  String(invcat ?? "").trim() === "11";

export const isNRIInvCat = (invcat?: string | null): boolean => {
  const v = String(invcat ?? "").trim();
  return v === "21" || v === "22";
};

// Maps invcat to the specific NRI bank account type. Returns null when invcat
// is not an NRI code (RI or unknown).
export const nriAccountTypeFromInvCat = (
  invcat?: string | null,
): "NRE" | "NRO" | null => {
  const v = String(invcat ?? "").trim();
  if (v === "21") return "NRE";
  if (v === "22") return "NRO";
  return null;
};

export interface FolioNriStatus {
  isNri: boolean;
  nriAccountType: "NRE" | "NRO" | null;
}

/**
 * Single source of truth for a folio's residency status.
 * Priority: folio `invcat` (authoritative) -> ResidentType -> Category/TaxStatus.
 * Keeps existing behavior intact when invcat is absent.
 */
export const resolveFolioNriStatus = (params: {
  invcat?: string | null;
  residentType?: string | null;
  category?: string | null;
  taxStatus?: string | null;
}): FolioNriStatus => {
  const invcat = String(params.invcat ?? "").trim();

  // 1. invcat is authoritative when present.
  if (invcat) {
    if (isRIInvCat(invcat)) return { isNri: false, nriAccountType: null };
    const acctFromInvCat = nriAccountTypeFromInvCat(invcat);
    if (acctFromInvCat) return { isNri: true, nriAccountType: acctFromInvCat };
    // Unknown invcat value — fall through to the legacy heuristics below.
  }

  // 2. ResidentType heuristic.
  const residentType = String(params.residentType ?? "").toUpperCase();
  if (residentType.includes("NRO")) return { isNri: true, nriAccountType: "NRO" };
  if (residentType.includes("NRE")) return { isNri: true, nriAccountType: "NRE" };

  // 3. Category / TaxStatus heuristic.
  const category = String(params.category ?? "").trim();
  const taxStatus = String(params.taxStatus ?? "").trim();
  const isNriByCategory = isNRICategory(category) || isNRICategory(taxStatus);
  if (residentType.includes("NRI") || isNriByCategory) {
    // Distinguish NRO vs NRE. Match the category code EXACTLY ("22") rather than
    // as a substring — a substring check would false-positive on values that
    // merely contain "22" (a year like 2022, an ID, an amount). Also match the
    // explicit "NON-REPATRIABLE" text.
    const catText = `${category} ${taxStatus}`.toUpperCase();
    const isNroByCode = category === "22" || taxStatus === "22";
    if (isNroByCode || catText.includes("NON-REPATRIABLE")) {
      return { isNri: true, nriAccountType: "NRO" };
    }
    // Default NRI account type is NRE (repatriable). This covers a generic "NRI"
    // ResidentType or an NRI category with no repatriable/non-repatriable marker,
    // where the specific type is unknown — NRE is the intended business default.
    return { isNri: true, nriAccountType: "NRE" };
  }

  return { isNri: false, nriAccountType: null };
};

export const isNRIApplicant = (
  applicant?: {
    category?: string | null;
    isTaxResident?: boolean | string | null;
  } | null,
): boolean => {
  if (!applicant) return false;
  if (applicant.isTaxResident === false || applicant.isTaxResident === "N") {
    return true;
  }
  return isNRICategory(applicant.category);
};

export const TRANSFER_OUT_MIN_BALANCE = 6000;

export const TRANSFER_OUT_MIN_BALANCE_ERROR =
  "Minimum account balance of ₹6,000 is required to proceed";

export const isTransferOutBalanceInsufficient = (currentAum?: number | null): boolean =>
  (currentAum || 0) < TRANSFER_OUT_MIN_BALANCE;

/** Count installments between start/end for a systematic plan frequency (STP/SWP mobile parity). */
export const calculateInstallmentsFromDateRange = (
  startDate: Date | string,
  endDate: Date | string,
  frequency: string,
): number => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return 2;

  const diffDays = end.diff(start, "day");
  let calculatedInstallments = 2;
  const freq = frequency.toLowerCase();

  if (freq === "daily") calculatedInstallments = Math.floor(diffDays / 1) + 1;
  else if (freq === "weekly") calculatedInstallments = Math.floor(diffDays / 7) + 1;
  else if (freq === "fortnightly") calculatedInstallments = Math.floor(diffDays / 14) + 1;
  else if (freq === "monthly") calculatedInstallments = end.diff(start, "month") + 1;
  else if (freq === "quarterly")
    calculatedInstallments = Math.floor(end.diff(start, "month") / 3) + 1;

  return calculatedInstallments;
};

export type FixedSystematicBalanceCheck = {
  valid: boolean;
  effectiveInstallments: number;
  totalRequiredAmount: number;
};

/** Validates total debit (amount × installments) does not exceed scheme balance. */
export const checkFixedSystematicTotalBalance = (params: {
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  frequency?: string | null;
  amount?: number | string | null;
  minAmount?: number;
  minInstallments?: number;
  currentBalance?: number;
}): FixedSystematicBalanceCheck => {
  const {
    startDate,
    endDate,
    frequency,
    amount,
    minAmount = 0,
    minInstallments = 1,
    currentBalance = 0,
  } = params;

  const calculatedInstallments =
    startDate && endDate && frequency
      ? calculateInstallmentsFromDateRange(startDate, endDate, frequency)
      : 2;

  const effectiveInstallments = Math.max(calculatedInstallments, minInstallments || 1);
  const amountToCheck = Math.max(Number(amount) || 0, minAmount || 0);
  const totalRequiredAmount = amountToCheck * effectiveInstallments;

  return {
    valid: totalRequiredAmount <= Number(currentBalance),
    effectiveInstallments,
    totalRequiredAmount,
  };
};

export const getFixedSystematicTotalBalanceErrorMessage = (
  totalRequiredAmount: number,
  effectiveInstallments: number,
  currentBalance: number,
): string =>
  `The total amount required (${numberToIndianCurrency(totalRequiredAmount)}) for ${effectiveInstallments} installments exceeds your available balance (${numberToIndianCurrency(Number(currentBalance))}). Please reduce the number of installments or add funds to continue.`;

export {
  resolveHolderIncomeCode,
  resolveHolderIncomeDesc,
  resolveHolderOccupationCode,
  resolveIncomeSlabCode,
  resolveIncomeSlabDesc,
  resolveOccupationCode
} from "./kycMasterCodes";

export {
  applyPrimaryHolderAddressToNominee,
  normalizeCountryForNomineeApi,
  resolvePrimaryHolderAddressForNominee
} from "./nomineeAddress";

export {
  buildMinSipMessage,
  compareTopUpProjection,
  computeSipEndDate,
  formatIndianRupee,
  installmentsPerYear,
  projectSip,
  SIP_PROJECTION_ANNUAL_RETURN
} from "./sipProjection";
export type {
  SipProjectionInput,
  SipProjectionResult,
  TopUpComparison
} from "./sipProjection";

export const sanitizeName = (name: string | undefined | null): string => {
  if (!name) return "";
  const cleaned = name.trim();
  const lower = cleaned.toLowerCase();
  if (
    lower === "test" ||
    lower === "test name" ||
    lower === "test investor" ||
    lower === "test user" ||
    lower === "user" ||
    lower === "null" ||
    lower === "undefined"
  ) {
    return "";
  }
  return cleaned;
};

export { isValidHolderName } from "./holderName";
export { resetEmailFields, resetMobileFields } from "./jointHolderFields";

export const filterOverseasCountries = <
  T extends {
    countryCode?: string;
    countryName?: string;
    label?: string;
    value?: string;
  },
>(
  list: T[] | undefined | null,
): T[] => {
  if (!list) return [];
  return list.filter((item) => {
    // Supports both API format ({ countryCode, countryName }) and mapped option format ({ label, value })
    const rawCode = item.countryCode ?? item.value ?? "";
    const rawName = item.countryName ?? item.label ?? "";

    const code = String(rawCode).trim().toUpperCase();
    const name = String(rawName).trim().toUpperCase();

    // Excludes India via ISO code (IN), abbreviation (IND), or standard name (INDIA)
    return code !== "IN" && code !== "IND" && code !== "INDIA" && name !== "INDIA";
  });
};

export {
  autoFormatDateInput,
  ConvertDOBToYYYYMMDD,
  getAgeFromDob,
  getMinorDobMinDate,
  isMinorByDob,
  parseDobInputToIso,
  parseMinorProfileDobToIso
} from "./date";

export { isAccountLockedError } from "./accountLock";

/**
 * Formats an EUIN for the combined-checkout API. The backend expects the EUIN
 * with the "E" prefix but WITHOUT the hyphen, e.g. "E123456" (not "E-123456").
 * Accepts bare digits, "E123456", or "E-123456" and normalizes to "E123456".
 * Returns "" for empty/nullish input.
 */
export const formatEuinForApi = (raw: string | undefined | null): string => {
  if (!raw) return "";
  const val = String(raw).trim().toUpperCase();
  if (!val) return "";
  // Strip any leading "E" and "-" then re-add a single "E" prefix (no hyphen).
  const digits = val.replace(/^E-?/, "");
  if (!digits) return "";
  return `E${digits}`;
};
